import React from 'react';
import { Link } from 'react-router-dom';

// ONE TOKENIZER, TWO OUTPUTS — render (React elements) and strip (plain text).
// Slice C of the reader-mint sitting; ROADMAP "markdown rendering is a SECURITY
// decision, not a formatting one".
//
// ── WHY THIS FILE IS SHAPED THE WAY IT IS ──────────────────────────────────
//
// The codebase's best security property is one nobody wrote down until the
// 2026-07-25 repo review found it: THERE IS NO HTML SINK IN THIS APPLICATION.
// Zero `dangerouslySetInnerHTML`, no inline frames, no markdown renderer, no
// sanitizer in either package manifest. Object content is plain text end to
// end, escaped by React at every render site. That property was true BY
// ACCIDENT, which is the same failure shape as a rule that exists only in a
// comment — someone adds markdown for entirely good reasons and it is gone,
// silently, with no defenses behind it.
//
// So this renderer produces REACT ELEMENTS and never an HTML string. There is
// no sanitizer here because there is nothing to sanitize: a `<script>` in the
// source text becomes a text node saying `<script>`, because React escapes
// every string it renders. Adding `dangerouslySetInnerHTML` to this file — or
// swapping this for a library that returns HTML — re-opens the sink and
// silently voids that guarantee for every surface at once.
//
// The one place an injection can still hide is a LINK, because an href is not
// text: `[click](javascript:alert(1))` would execute on click. Hence the scheme
// allowlist below. A link whose scheme is not http(s) renders as literal text,
// never as an anchor, and never with a stripped-but-clickable href.
//
// ── WHAT IS DELIBERATELY ABSENT ────────────────────────────────────────────
//
//   - Images. An <img> is an outbound request to an arbitrary host at render
//     time, which turns any object into a tracking pixel for whoever wrote it.
//   - Raw HTML passthrough, in any form.
//   - Reference links, tables, footnotes, nested lists: not in the driven
//     subset (M-1, accepted as shown 2026-08-07). Add them by extending this
//     tokenizer, never by reaching for a library that returns markup.
//
// ── ANCHOR-BEARING TEXT NEVER COMES HERE ───────────────────────────────────
//
// The Reader's document body renders RAW, by rule. `claim_anchors` offsets
// index the STORED text; rendered markdown has different characters in
// different places, so an anchored underline would drift off its sentence.
// This is not a preference, it is the reason the ruling says read-time.

// ── the inline grammar ─────────────────────────────────────────────────────
// Order matters: the relation form must be tried before the bare wikilink, or
// `supports::[[x]]` would match as a wikilink with a stray prefix.
const INLINE = [
  { key: 'rel', re: /(\w[\w-]*)::\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/ },
  { key: 'wiki', re: /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/ },
  { key: 'link', re: /\[([^\]]+)\]\(([^)\s]+)\)/ },
  { key: 'code', re: /`([^`]+)`/ },
  { key: 'strong', re: /\*\*([^*]+)\*\*/ },
  { key: 'em', re: /\*([^*\n]+)\*/ },
];

// Only these may become an href. Everything else renders as text — including
// `javascript:`, `data:` and `vbscript:`, which are the ones that execute.
const SAFE_SCHEME = /^https?:\/\//i;

/** Split one line into tokens. Pure, and shared by both outputs. */
function tokenize(text) {
  const out = [];
  let rest = String(text ?? '');
  while (rest) {
    let best = null;
    for (const rule of INLINE) {
      const m = rule.re.exec(rest);
      if (m && (best === null || m.index < best.m.index)) best = { rule, m };
    }
    if (!best) {
      out.push({ type: 'text', value: rest });
      break;
    }
    if (best.m.index > 0) out.push({ type: 'text', value: rest.slice(0, best.m.index) });
    const [full, a, b, c] = best.m;
    switch (best.rule.key) {
      case 'rel':
        out.push({ type: 'rel', name: a, target: b, display: c || b });
        break;
      case 'wiki':
        out.push({ type: 'wiki', target: a, display: b || a });
        break;
      case 'link':
        // An unsafe scheme is not "sanitized" into a safe link — it stops being
        // a link at all, and the reader sees exactly what was written.
        if (SAFE_SCHEME.test(b)) out.push({ type: 'link', text: a, href: b });
        else out.push({ type: 'text', value: full });
        break;
      case 'code':
        out.push({ type: 'code', value: a });
        break;
      case 'strong':
        out.push({ type: 'strong', value: a });
        break;
      case 'em':
        out.push({ type: 'em', value: a });
        break;
      default:
        out.push({ type: 'text', value: full });
    }
    rest = rest.slice(best.m.index + full.length);
  }
  return out;
}

function inlineNodes(text, keyBase) {
  return tokenize(text).map((t, i) => {
    const key = `${keyBase}-${i}`;
    switch (t.type) {
      case 'strong':
        return <strong key={key}>{t.value}</strong>;
      case 'em':
        return <em key={key}>{t.value}</em>;
      case 'code':
        return <code key={key} className="wv-md-code">{t.value}</code>;
      case 'link':
        // rel=noopener: a target=_blank link otherwise hands the opened page a
        // live `window.opener` reference back into this app.
        return (
          <a key={key} href={t.href} target="_blank" rel="noopener noreferrer" className="wv-md-link">
            {t.text}
          </a>
        );
      case 'wiki':
        return (
          <Link key={key} to={`/object/${t.target}`} className="wv-md-wiki">
            {t.display}
          </Link>
        );
      case 'rel':
        // Display only. The EDGE is drawn at save time by the composer, never
        // by the renderer — rendering must not have side effects on the graph.
        return (
          <span key={key} className="wv-md-rel">
            <b>{t.name.replace(/_/g, ' ')}</b>{' '}
            <Link to={`/object/${t.target}`}>{t.display}</Link>
          </span>
        );
      default:
        return <React.Fragment key={key}>{t.value}</React.Fragment>;
    }
  });
}

/** Plain text, syntax removed. For feed rows, edge sentences, tree rows. */
export function stripMarkdown(text) {
  return String(text ?? '')
    .split('\n')
    .map((line) =>
      tokenize(line.replace(/^\s{0,3}(#{1,3}\s+|>\s+|[-*]\s+)/, ''))
        .map((t) => {
          switch (t.type) {
            case 'rel':
              return `${t.name.replace(/_/g, ' ')} «${t.display}»`;
            case 'wiki':
              return t.display;
            case 'link':
              return t.text;
            default:
              return t.value;
          }
        })
        .join('')
    )
    .filter((l) => l.trim())
    .join(' ')
    .trim();
}

/**
 * Render the subset as React elements. Never returns HTML, never touches
 * dangerouslySetInnerHTML — see the header.
 */
export function Markdown({ text, className }) {
  const src = String(text ?? '');
  const blocks = [];
  let list = null;
  const flushList = () => {
    if (list) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="wv-md-ul">
          {list.map((item, i) => (
            <li key={i}>{inlineNodes(item, `li-${blocks.length}-${i}`)}</li>
          ))}
        </ul>
      );
      list = null;
    }
  };

  src.split('\n').forEach((raw, i) => {
    const line = raw.replace(/\s+$/, '');
    const li = /^\s{0,3}[-*]\s+(.*)$/.exec(line);
    if (li) {
      (list = list || []).push(li[1]);
      return;
    }
    flushList();
    if (!line.trim()) return;
    const h = /^\s{0,3}(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      // Headings map to h3/h4 rather than h1/h2: this renders INSIDE a page
      // that already owns its heading levels, and a stray h1 in body content
      // breaks the document outline for anyone navigating by headings.
      const Tag = h[1].length >= 3 ? 'h4' : 'h3';
      blocks.push(
        <Tag key={`h-${i}`} className="wv-md-h">
          {inlineNodes(h[2], `h-${i}`)}
        </Tag>
      );
      return;
    }
    const q = /^\s{0,3}>\s?(.*)$/.exec(line);
    if (q) {
      blocks.push(
        <blockquote key={`q-${i}`} className="wv-md-quote">
          {inlineNodes(q[1], `q-${i}`)}
        </blockquote>
      );
      return;
    }
    blocks.push(
      <p key={`p-${i}`} className="wv-md-p">
        {inlineNodes(line, `p-${i}`)}
      </p>
    );
  });
  flushList();

  return <div className={`wv-md${className ? ` ${className}` : ''}`}>{blocks}</div>;
}

export default Markdown;
